import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ExternalLink, ArrowRight, Loader2, Filter,
  Calendar, Building2, Globe, CheckCircle2
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';

const References = () => {
  const { language } = useLanguage();
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [services, setServices] = useState([]);

  useEffect(() => {
    loadReferences();
    loadServices();
  }, []);

  const loadReferences = async () => {
    try {
      const { data, error } = await supabase
        .from('our_references')
        .select('*')
        .eq('is_active', true)
        .order('is_featured', { ascending: false })
        .order('completed_at', { ascending: false });

      if (!error && data) {
        setReferences(data);
      }
    } catch (err) {
      console.error('Error loading references:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadServices = async () => {
    try {
      const { data } = await supabase
        .from('services')
        .select('slug, title_tr, title_en')
        .eq('is_active', true);
      if (data) setServices(data);
    } catch (err) {
      console.error('Error loading services:', err);
    }
  };

  const filteredReferences = filter === 'all'
    ? references
    : references.filter(ref => {
      const servicesUsed = ref.services_used || [];
      return servicesUsed.includes(filter);
    });

  const texts = {
    tr: {
      title: 'Referanslarımız',
      subtitle: 'Başarı hikayelerimizi keşfedin',
      description: 'Farklı sektörlerden müşterilerimizle birlikte gerçekleştirdiğimiz projeleri inceleyin.',
      allProjects: 'Tüm Projeler',
      viewProject: 'Projeyi İncele',
      visitSite: 'Siteyi Ziyaret Et',
      featured: 'Öne Çıkan',
      noReferences: 'Henüz referans eklenmemiş.',
      completedDate: 'Tamamlanma',
      client: 'Müşteri',
      technologies: 'Teknolojiler',
      getQuote: 'Teklif Al'
    },
    en: {
      title: 'Our References',
      subtitle: 'Discover our success stories',
      description: 'Explore the projects we have completed with our clients from various industries.',
      allProjects: 'All Projects',
      viewProject: 'View Project',
      visitSite: 'Visit Site',
      featured: 'Featured',
      noReferences: 'No references added yet.',
      completedDate: 'Completed',
      client: 'Client',
      technologies: 'Technologies',
      getQuote: 'Get Quote'
    }
  };

  const t = texts[language] || texts.tr;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <SEO
          title={language === 'tr' ? 'Referanslarımız & Başarı Hikayeleri' : 'Our References & Success Stories'}
          description={language === 'tr' ? 'Cplus Media LLC tarafından tamamlanan projeler ve mutlu müşteriler. E-ticaret, Web Yazılım ve Dijital Pazarlama referanslarımız.' : 'Explore Cplus Media LLC success stories and portfolio. E-commerce, Web Development and Digital Marketing projects.'}
          keywords="referanslar, başarı hikayeleri, portfolio, web tasarım referansları, reklam ajansı işleri, cplus media llc"
        />
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="references-page">
      <SEO
        title={language === 'tr' ? 'Referanslarımız & Başarı Hikayeleri' : 'Our References & Success Stories'}
        description={language === 'tr' ? 'Cplus Media LLC tarafından tamamlanan projeler ve mutlu müşteriler. E-ticaret, Web Yazılım ve Dijital Pazarlama referanslarımız.' : 'Explore Cplus Media LLC success stories and portfolio. E-commerce, Web Development and Digital Marketing projects.'}
        keywords="referanslar, başarı hikayeleri, portfolio, web tasarım referansları, reklam ajansı işleri, cplus media llc"
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto mb-4"
          >
            {t.subtitle}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 max-w-2xl mx-auto"
          >
            {t.description}
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Filter Section */}
        {services.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap items-center gap-3 mb-12"
          >
            <Filter className="w-5 h-5 text-gray-400" />
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-300'
                }`}
              data-testid="filter-all"
            >
              {t.allProjects}
            </button>
            {services.map(service => (
              <button
                key={service.slug}
                onClick={() => setFilter(service.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${filter === service.slug
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-blue-300'
                  }`}
                data-testid={`filter-${service.slug}`}
              >
                {language === 'tr' ? service.title_tr : service.title_en}
              </button>
            ))}
          </motion.div>
        )}

        {/* References Grid */}
        {filteredReferences.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">{t.noReferences}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="references-grid">
            {filteredReferences.map((ref, idx) => {
              const title = language === 'tr' ? ref.title_tr : ref.title_en;
              const excerpt = language === 'tr' ? ref.excerpt_tr : ref.excerpt_en;
              const technologies = ref.technologies || [];
              const servicesUsed = ref.services_used || [];

              return (
                <motion.div
                  key={ref.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="group"
                  data-testid={`reference-card-${ref.slug}`}
                >
                  <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                    {/* Image */}
                    <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 overflow-hidden">
                      {ref.featured_image_url ? (
                        <img
                          src={ref.featured_image_url}
                          alt={title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building2 className="w-16 h-16 text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                      {ref.is_featured && (
                        <div className="absolute top-3 left-3 px-3 py-1 bg-yellow-500 text-white text-xs font-bold rounded-full">
                          {t.featured}
                        </div>
                      )}
                      {ref.client_logo_url && (
                        <div className="absolute bottom-3 right-3 w-12 h-12 bg-white dark:bg-gray-800 rounded-lg p-2 shadow-lg">
                          <img
                            src={ref.client_logo_url}
                            alt={ref.client_name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-6 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
                        <Building2 className="w-4 h-4" />
                        <span>{ref.client_name}</span>
                        {ref.completed_at && (
                          <>
                            <span>•</span>
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(ref.completed_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { year: 'numeric', month: 'short' })}</span>
                          </>
                        )}
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        {title}
                      </h3>

                      <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 flex-1">
                        {excerpt}
                      </p>

                      {/* Technologies */}
                      {technologies.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {technologies.slice(0, 4).map((tech, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded"
                            >
                              {tech}
                            </span>
                          ))}
                          {technologies.length > 4 && (
                            <span className="px-2 py-1 text-xs font-medium text-gray-500">
                              +{technologies.length - 4}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                        <Link
                          to={`/references/${ref.slug}`}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                          data-testid={`view-reference-${ref.slug}`}
                        >
                          {t.viewProject}
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                        {ref.project_url && (
                          <a
                            href={ref.project_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            title={t.visitSite}
                          >
                            <ExternalLink className="w-5 h-5" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-12">
            <CheckCircle2 className="w-12 h-12 text-white/80 mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {language === 'tr' ? 'Sizin de bir projeniz mi var?' : 'Do you have a project too?'}
            </h2>
            <p className="text-indigo-100 mb-8 max-w-2xl mx-auto">
              {language === 'tr'
                ? 'Referanslarımız arasına katılın. Projenizi birlikte hayata geçirelim.'
                : 'Join our references. Let us bring your project to life together.'}
            </p>
            <Link
              to="/portal/new-quote"
              className="inline-flex items-center px-8 py-4 bg-white text-indigo-600 rounded-xl font-bold hover:shadow-xl hover:-translate-y-1 transition-all"
              data-testid="references-cta-btn"
            >
              {t.getQuote}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default References;
